import React from 'react';
import Loader from 'src/components/Loader';
import Overlay from 'src/components/Overlay';
import Empty from 'src/components/Empty';
import Table from 'src/components/Table';
import { useTablePagination } from 'src/SharedCustomHooks/UseTablePagination/useTablePagination';
import ReusableLightbox from 'src/components/ReusableLightbox/reusable-lightbox';
import { PATHS } from 'src/common/constants';
import { ServiceRequestDashboardActions } from 'src/pages/ServiceRequests/components/ServiceRequestDashboardActions';
import { DownloadCSVAndPDFParserOverlay } from 'src/utils/DownloadCSVAndPDFParserOverlay/DownloadCSVAndPDFParserOverlay';
import 'src/pages/ServiceRequests/ServiceRequestDashBoardTable.scss';
import {
  useCommonAreaServiceRequestsOrchestrator,
  UseCommonAreaServiceRequestsOrchestratorReturn,
} from './CustomHooks/CommonAreaServiceRequests/useCommonAreaServiceRequestsOrchestrator';

interface CommonAreaServiceRequestsProps {
  commonArea: any;
  property: any;
}

const CommonAreaServiceRequests = ({
  commonArea,
  property,
}: CommonAreaServiceRequestsProps) => {
  const commonAreaId: string = commonArea?.id ?? '';
  const propertyId: string = property?.id ?? '';
  const navigateTo: string = PATHS.COMMON_AREA_VIEW.replace(
    ':id',
    `${commonAreaId}&${propertyId}`
  );
  const navLabel: string = commonArea?.name ?? '';

  const orchestrator: UseCommonAreaServiceRequestsOrchestratorReturn =
    useCommonAreaServiceRequestsOrchestrator({
      commonAreaId,
      navigateTo,
      navLabel,
    });

  const { itemsPerPage, onItemsPerPageChange } = useTablePagination();

  const isInitialLoading: boolean =
    orchestrator.isLoading && orchestrator.serviceRequests.length === 0;

  if (isInitialLoading) {
    return <Loader />;
  }

  const showEmpty: boolean =
    !orchestrator.isLoading && orchestrator.visibleServiceRequests.length === 0;

  return (
    <div>
      <Overlay showOverlay={orchestrator.isLoading} transparent={true} centre>
        <Loader style={{ margin: 0 }} />
      </Overlay>

      <DownloadCSVAndPDFParserOverlay
        isOpen={orchestrator.isDownloadOverlayOpen}
        onClose={orchestrator.closeDownloadOverlay}
        onDownloadCSV={orchestrator.onDownloadCSV}
        isCSVLoading={orchestrator.isCSVLoading}
        onDownloadPDF={orchestrator.onDownloadPDF}
        isPDFLoading={orchestrator.isPDFLoading}
      />

      <ServiceRequestDashboardActions
        searchString={orchestrator.searchQuery}
        onChangeSearchString={orchestrator.setSearchQuery}
        showResolved={orchestrator.showResolvedOnly}
        onShowResolved={orchestrator.toggleShowResolved}
        onDownload={orchestrator.openDownloadOverlay}
        hideBulkActions={true}
      />

      {showEmpty ? (
        <Empty
          direction="ttb"
          headline="No service requests for this common area"
          strapline="Service requests reported against this common area will be listed here."
          image={null}
          searchIsActive={orchestrator.searchQuery !== ''}
        />
      ) : (
        <>
          <Table
            expandedGroups={orchestrator.expandedGroups}
            defaultSort={{
              accessor: 'createdDate',
              direction: 'ascending',
            }}
            columns={orchestrator.columns}
            data={orchestrator.visibleServiceRequests}
            stickyHeader
            paginate={{ itemsPerPage, position: 'both' }}
            onItemsPerPageChange={onItemsPerPageChange}
          />
          <ReusableLightbox {...orchestrator.lightboxProps} />
        </>
      )}
    </div>
  );
};

export default CommonAreaServiceRequests;
